import React, {Fragment, useEffect, useRef, useState} from 'react';
import Layout from '@components/Layout';
import styles from './styles.module.scss';
import { Grid, Divider, Col, Row } from '@components/Grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStaticQuery, graphql } from 'gatsby';
import fuzzysearch from 'fuzzysearch';
import Select from '@components/Select';
import Map from '@components/Map';
import Card from '@components/Card';
import Input from '@components/Input';
import Button from '@components/Button';
import List from '@components/List';
import SEO from '@components/SEO';
import Alert from '@components/Alert';
const Dealers = ({
    data: {
        page: {
            frontmatter: { title, findClosestDealersText, closestDealersTitle },
        },
        dealers: { edges: dealers },
    },
}) => {
    const [query, setQuery] = useState('');
    dealers = dealers?.map(({ node: { frontmatter } }) => frontmatter);
    const searchedDealers = query
        ? dealers.filter(({ name }) => {
              return fuzzysearch(query.toLowerCase(), name.toLowerCase());
          })
        : [];
    const [dealer, setDealer] = useState(null);
    const map = useRef(null);
    const $map = useRef(null);
    const [viewport, setViewport] = useState({
        latitude: 36,
        longitude: -94.23017918635317,
        zoom: 4,
        panned: false,
    });
    const [requestingGeolocation, setRequestingGeolocation] = useState(false);
    const [geolocation, setGeolocation] = useState(null);
    const [closestDealers, setClosestDealers] = useState(null);
    const [requestingGeolocationError, setRequestingGeolocationError] = useState(false);

    const handleRequestGeolocation = () => {
        if (typeof window !== 'undefined') {
            setQuery('');
            setDealer(null);
            setGeolocation(null);
            setRequestingGeolocation(true);
            navigator?.geolocation?.getCurrentPosition(
                location => {
                    setRequestingGeolocationError(false);
                    setGeolocation(location.coords);
                    setRequestingGeolocation(false);
                },
                error => {
                    setRequestingGeolocation(false);
                    setRequestingGeolocationError(true);
                }
            );
        }
    };

    useEffect(() => {
        if (!query) return;
        setDealer(null);
    }, [query]);

    useEffect(() => {
        if (!dealer) return;

        setQuery('');
        map.current?.handleViewportChange(
            {
                longitude: dealer.longitude,
                latitude: dealer.latitude,
                zoom: 12,
            },
            { isPanning: true }
        );
    }, [dealer]);

    useEffect(() => {
        if (!geolocation) return;

        //This function takes in latitude and longitude of two location and returns the distance between them as the crow flies (in km)
        function calcDistance(lat, lon) {
            const R = 6371; // km
            const dLat = toRad(lat - geolocation.latitude);
            const dLon = toRad(lon - geolocation.longitude);
            const lat1 = toRad(geolocation.latitude);
            const lat2 = toRad(lat);

            const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c * 0.621371;
        }

        // Converts numeric degrees to radians
        const toRad = value => (value * Math.PI) / 180;

        const dealersWithDistances = dealers
            .filter(({ name }) => !!name)
            .map(dealer => ({
                ...dealer,
                distance: calcDistance(dealer.latitude, dealer.longitude),
            }));

        setClosestDealers(dealersWithDistances.sort((a, b) => a.distance - b.distance).slice(0, 5));
    }, [geolocation]);

    const markers = (query ? searchedDealers : dealers)?.map(
        ({ longitude, latitude, ...data }) => ({
            key: `${data.name}_${longitude}_${latitude}`,
            text: data.name,
            coordinates: [longitude, latitude],
            data: {
                ...data,
                longitude,
                latitude,
            },
        })
    );

    return (
        <Layout>
            <SEO title={title} />
            <Grid>
                <h1>{title}</h1>
                <Divider />
                <Row>
                    <Col xs={12} lg={6}>
                        <Input
                            secondary
                            value={query}
                            placeholder="Search"
                            icon="search"
                            onChange={e => setQuery(e.target.value)}
                        />
                        <Divider margin={2} />
                    </Col>
                    <Col xs={12} lg={6}>
                        <Button
                            iconBefore
                            icon="location-arrow"
                            text={findClosestDealersText}
                            onClick={handleRequestGeolocation}
                            submitting={requestingGeolocation}
                        />
                        {requestingGeolocationError && (
                            <Fragment>
                                <Divider margin={2} />
                                <Alert
                                    type="error"
                                    message="You have denied access to us finding your location, please ensure your browser has enabled location tracking."
                                />
                            </Fragment>
                        )}
                        <Divider margin={2} />
                    </Col>
                </Row>
            </Grid>

            <div className={styles.map}>
                <Map
                    height={600}
                    ref={map}
                    $map={$map}
                    longitude={viewport.longitude}
                    latitude={viewport.latitude}
                    zoom={viewport.zoom}
                    bounds={[
                        [-171.791110603, 18.91619],
                        [-66.96466, 71.3577635769],
                    ]}
                    onViewportChange={viewport => setViewport(viewport)}
                    markers={markers}
                    onMarkerFocus={marker => {
                        setDealer(marker.data);
                    }}
                    onMarkerBlur={() => setDealer(null)}
                />
                {!!query ? (
                    <div className={styles.details}>
                        <Grid>
                            <Card depth={3}>
                                <Card.Title>Search results</Card.Title>
                                <Card.Content>
                                    <List>
                                        {!!searchedDealers && !!searchedDealers.length ? (
                                            searchedDealers.map(dealer => (
                                                <List.Item
                                                    key={`${dealer.longitude}_${dealer.latitude}`}
                                                    onClick={() => setDealer(dealer)}
                                                >
                                                    <List.Item.Column>
                                                        <List.Item.Title text={dealer?.name} />
                                                    </List.Item.Column>
                                                    <List.Item.Column>
                                                        <List.Item.Title
                                                            bold={false}
                                                            text={dealer.address}
                                                        />
                                                        <List.Item.Description
                                                            text={`${dealer.city}, ${dealer.zipCode}`}
                                                        />
                                                    </List.Item.Column>
                                                </List.Item>
                                            ))
                                        ) : (
                                            <List.Item key="no-search-results">
                                                <List.Item.Column>
                                                    <List.Item.Title text="No results found" />
                                                </List.Item.Column>
                                            </List.Item>
                                        )}
                                    </List>
                                </Card.Content>
                            </Card>
                        </Grid>
                    </div>
                ) : !!dealer ? (
                    <div className={styles.details}>
                        <Grid>
                            <Card depth={3}>
                                <Card.Title>
                                    {dealer.name}
                                    <Card.Title.Description link={dealer?.website}>
                                        {dealer?.website}
                                    </Card.Title.Description>
                                </Card.Title>
                                <Card.Content>
                                    <Row>
                                        <Col xs={12} lg={7}>
                                            <p>
                                                Lorem ipsum dolor sit amet, consetetur sadipscing
                                                elitr, sed diam nonumy eirmod tempor invidunt ut
                                                labore et dolore magna aliquyam.Lorem ipsum dolor
                                                sit amet, consetetur sadipscing elitr, sed diam
                                                nonumy eirmod tempor invidunt ut labore et dolore
                                                magna aliquyam.Lorem ipsum dolor sit amet,
                                                consetetur sadipscing elitr, sed diam nonumy eirmod
                                                tempor invidunt ut labore et dolore magna
                                                aliquyam.Lorem ipsum dolor sit amet, consetetur
                                                sadipscing elitr, sed diam nonumy eirmod tempor
                                                invidunt ut labore et dolore magna aliquyam.
                                            </p>
                                        </Col>
                                        <Col xs={12} lg={5}>
                                            <div className={styles.address}>
                                                <p>{dealer?.address}</p>
                                                <p>{`${dealer?.city}, ${dealer?.province}`}</p>
                                                <p>{dealer?.zipCode}</p>
                                                <p>
                                                    {dealer?.phoneNumber && (
                                                        <a
                                                            href={`tel:{${dealer?.phoneNumber}`}
                                                            rel="noopener noreferrer"
                                                        >
                                                            {dealer?.phoneNumber}
                                                        </a>
                                                    )}
                                                </p>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Content>
                                {!!dealer?.website && (
                                    <Card.Actions>
                                        <Card.Actions.Action text="Visit" link={dealer?.website} />
                                    </Card.Actions>
                                )}
                            </Card>
                        </Grid>
                    </div>
                ) : (
                    !!closestDealers && (
                        <div className={styles.details}>
                            <Grid>
                                <div style={{ zIndex: 1000 }}>
                                    <Card depth={3}>
                                        <Card.Title>{closestDealersTitle}</Card.Title>
                                        <Card.Content>
                                            <List>
                                                {closestDealers.map(dealer => (
                                                    <List.Item
                                                        key={`${dealer.longitude}_${dealer.latitude}`}
                                                        title={dealer.name}
                                                        onClick={() => setDealer(dealer)}
                                                    >
                                                        <List.Item.Column>
                                                            <List.Item.Title text={dealer?.name} />
                                                            <List.Item.Description
                                                                text={`${dealer.distance.toFixed(
                                                                    2
                                                                )} Miles`}
                                                            />
                                                        </List.Item.Column>
                                                        <List.Item.Column>
                                                            <List.Item.Title
                                                                bold={false}
                                                                text={dealer?.address ?? ''}
                                                            />
                                                            <List.Item.Description
                                                                text={`${dealer?.city ??
                                                                    ''}, ${dealer?.zipCode ??
                                                                    ''}, ${dealer?.province ?? ''}`}
                                                            />
                                                        </List.Item.Column>
                                                    </List.Item>
                                                ))}
                                            </List>
                                        </Card.Content>
                                    </Card>
                                </div>
                            </Grid>
                        </div>
                    )
                )}
            </div>
        </Layout>
    );
};

export default Dealers;

export const query = graphql`
    query Dealers($path: String!) {
        page: markdownRemark(frontmatter: { path: { eq: $path } }) {
            frontmatter {
                title: dealers_title
                findClosestDealersText: dealers_find_closest_dealers_text
                closestDealersTitle: dealers_closest_dealers_title
            }
        }
        dealers: allMarkdownRemark(
            filter: { fileAbsolutePath: { regex: "/data/dealers-locations/(?!.md$)/" } }
        ) {
            edges {
                node {
                    frontmatter {
                        name: dealer_name
                        phoneNumber: dealer_phone_number
                        website: dealer_website
                        longitude: dealer_longitude
                        latitude: dealer_latitude
                        address: dealer_address
                        city: dealer_city
                        province: dealer_province
                        zipCode: dealer_zip_code
                    }
                }
            }
        }
    }
`;
